import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Circle, Marker, InfoWindow, Autocomplete } from '@react-google-maps/api';
import { UrlApi } from '../../../services/apirest';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Tooltip,
  Stack
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PolygonIcon from '@mui/icons-material/AccountTree';
import CircleIcon from '@mui/icons-material/RadioButtonChecked';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';

const libraries = ['places', 'geometry'];
const MAP_HEIGHT = 620;
const POLYGON_COLOR = '#4285F4';
const CIRCLE_COLOR = '#34A853';

const defaultCenter = {
  lat: 19.4326,
  lng: -99.1332
};

const drawingOptions = {
  polygonOptions: {
    fillColor: POLYGON_COLOR,
    fillOpacity: 0.3,
    strokeWeight: 2,
    strokeColor: POLYGON_COLOR,
    clickable: true,
    editable: true,
    draggable: true,
    zIndex: 1
  },
  circleOptions: {
    fillColor: CIRCLE_COLOR,
    fillOpacity: 0.3,
    strokeWeight: 2,
    strokeColor: CIRCLE_COLOR,
    clickable: true,
    editable: true,
    draggable: true,
    zIndex: 1
  }
};

function GeoZonas(props) {
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [geozonas, setGeozonas] = useState([]);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [activeGeozona, setActiveGeozona] = useState(null);
  const [drawingMode, setDrawingMode] = useState(null);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [currentShape, setCurrentShape] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isCreating, setIsCreating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const autocompleteRef = useRef(null);
  const mapRef = useRef();
  const googleMapsApiKey = process.env.REACT_APP_Google_maps_key;
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries
  });

  useEffect(() => {
    loadGeozonas();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCenter(userLocation);
        },
        (err) => {
          console.error("Error obteniendo ubicación:", err);
          showSnackbar("No se pudo obtener tu ubicación. Usando ubicación por defecto.", 'warning');
        }
      );
    } else {
      showSnackbar("Geolocalización no soportada por tu navegador. Usando ubicación por defecto.", 'warning');
    }
  }, []);

  const loadGeozonas = async () => {
    try {
      const response = await fetch(`${UrlApi}/geozonas/xgrupo/${props.dataGrupo.id}`);
      if (!response.ok) throw new Error('Error al cargar geozonas');
      const data = await response.json();
      setGeozonas(data);
      setLoading(false);
    } catch (err) {
      console.error("Error cargando geozonas:", err);
      showSnackbar("Error al cargar las geozonas existentes", 'error');
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMap(map);
  };

  const startCreatingZone = (mode) => {
    if (currentShape || drawingPoints.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      toggleDrawingMode(mode);
    }
  };

  const confirmCancelDrawing = () => {
    cancelDrawing();
    setConfirmDialogOpen(false);
  };

  const cancelDrawing = () => {
    setCurrentShape(null);
    setDrawingPoints([]);
    setDrawingMode(null);
    setIsCreating(false);
    setNombre('');
    setDescripcion('');
  };

  const toggleDrawingMode = (mode) => {
    setCurrentShape(null);
    setDrawingPoints([]);
    setDrawingMode(drawingMode === mode ? null : mode);
  };

  const addDrawingPoint = (point) => {
    setDrawingPoints((prev) => [...prev, point]);
  };

  const undoLastPoint = () => {
    setDrawingPoints((prev) => prev.slice(0, -1));
  };

  const finishPolygon = () => {
    if (drawingPoints.length < 3) {
      showSnackbar('Se necesitan al menos 3 puntos para cerrar el polígono', 'warning');
      return;
    }
    setCurrentShape({ type: 'polygon', coordinates: drawingPoints });
    setDrawingMode(null);
    setIsCreating(true);
  };

  const saveGeozona = async () => {
    if (!currentShape || !nombre) {
      showSnackbar('El nombre es requerido', 'error');
      return;
    }

    try {
      let geozonaData = {
        nombre,
        descripcion,
        grupo: props.dataGrupo.id,
        type: currentShape.type,
        coordinates: [],
        center: null,
        radius: null
      };

      if (currentShape.type === 'polygon') {
        geozonaData.coordinates = currentShape.coordinates;
      } else if (currentShape.type === 'circle') {
        geozonaData.center = currentShape.center;
        geozonaData.radius = currentShape.radius;
        geozonaData.coordinates = [currentShape.center];
      }

      const response = await fetch(`${UrlApi}/geozonas/zona`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(geozonaData)
      });

      if (!response.ok) throw new Error('Error al guardar');

      const result = await response.json();

      const newGeozona = {
        ...geozonaData,
        id: result.geofenceId,
        created_at: new Date().toISOString()
      };

      setGeozonas([...geozonas, newGeozona]);
      cancelDrawing();
      showSnackbar('Zona guardada exitosamente', 'success');
    } catch (err) {
      console.error("Error guardando geozona:", err);
      showSnackbar("Error al guardar la geozona", 'error');
    }
  };

  const deleteGeozona = async (id) => {
    try {
      const response = await fetch(`${UrlApi}/geozonas/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      setGeozonas(geozonas.filter(zone => zone.id !== id));
      setActiveGeozona(null);
      showSnackbar('Zona eliminada exitosamente', 'success');
    } catch (err) {
      console.error("Error eliminando geozona:", err);
      showSnackbar("Error al eliminar la geozona", 'error');
    }
  };

  const onShapeClick = (geozona) => {
    setActiveGeozona(geozona);
    mapRef.current.panTo(getShapeCenter(geozona));
  };

  const onMapClick = (e) => {
    if (drawingMode === 'polygon') {
      addDrawingPoint({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      return;
    }
    setActiveGeozona(null);
  };

  const getShapeCenter = (geozona) => {
    if (geozona.type === 'circle') {
      return geozona.center;
    } else {
      let lat = 0, lng = 0;
      geozona.coordinates.forEach(point => {
        lat += point.lat;
        lng += point.lng;
      });
      return {
        lat: lat / geozona.coordinates.length,
        lng: lng / geozona.coordinates.length
      };
    }
  };

  const handleClose = () => {
    props.onClose(false);
  };

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current !== null) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setCenter(location);
        if (mapRef.current) {
          mapRef.current.panTo(location);
          mapRef.current.setZoom(15);
        }
      } else {
        showSnackbar('No se encontraron coordenadas para este lugar', 'warning');
      }
    }
  };

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
  };

  const locateUser = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCenter(userLocation);
          if (mapRef.current) {
            mapRef.current.panTo(userLocation);
            mapRef.current.setZoom(15);
          }
        },
        (err) => {
          console.error("Error obteniendo ubicación:", err);
          showSnackbar("No se pudo obtener tu ubicación", 'warning');
        }
      );
    } else {
      showSnackbar("Geolocalización no soportada por tu navegador", 'warning');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8' }}>
      <AppBar position="relative" elevation={0} sx={{ bgcolor: '#0979b0' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <MapIcon sx={{ ml: 1, mr: 1.5, opacity: 0.85 }} />
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            Zonas del Grupo {props.dataGrupo.nombre}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {!isLoaded ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: 400
          }}>
            <CircularProgress />
            <Typography color="text.secondary">Cargando mapa...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Paper
              elevation={2}
              sx={{
                position: 'relative',
                flex: '1 1 620px',
                minWidth: 320,
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              {/* Barra de búsqueda flotante */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  right: 16,
                  zIndex: 1,
                  display: 'flex',
                  gap: 1
                }}
              >
                <Autocomplete
                  onLoad={onLoad}
                  onPlaceChanged={onPlaceChanged}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Buscar localidad..."
                    value={searchText}
                    onChange={handleSearchTextChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }
                    }}
                  />
                </Autocomplete>
                <Tooltip title="Usar mi ubicación">
                  <Button
                    variant="contained"
                    onClick={locateUser}
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      backgroundColor: 'white',
                      color: 'primary.main',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      '&:hover': {
                        backgroundColor: 'grey.100'
                      }
                    }}
                  >
                    <MyLocationIcon />
                  </Button>
                </Tooltip>
              </Box>

              {loading ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  height: MAP_HEIGHT
                }}>
                  <CircularProgress />
                  <Typography color="text.secondary">Cargando zonas...</Typography>
                </Box>
              ) : (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: MAP_HEIGHT }}
                  zoom={15}
                  center={center}
                  onClick={onMapClick}
                  onLoad={onMapLoad}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    styles: [
                      {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                      }
                    ]
                  }}
                >
                  {geozonas.map((geozona) => (
                    <React.Fragment key={geozona.id}>
                      {geozona.type === 'polygon' ? (
                        <Polygon
                          paths={geozona.coordinates}
                          options={{
                            fillColor: POLYGON_COLOR,
                            fillOpacity: 0.3,
                            strokeColor: POLYGON_COLOR,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            clickable: true
                          }}
                          onClick={() => onShapeClick(geozona)}
                        />
                      ) : (
                        <Circle
                          center={geozona.center}
                          radius={geozona.radius}
                          options={{
                            fillColor: CIRCLE_COLOR,
                            fillOpacity: 0.3,
                            strokeColor: CIRCLE_COLOR,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            clickable: true
                          }}
                          onClick={() => onShapeClick(geozona)}
                        />
                      )}
                      {activeGeozona && activeGeozona.id === geozona.id && (
                        <InfoWindow
                          position={getShapeCenter(geozona)}
                          onCloseClick={() => setActiveGeozona(null)}
                        >
                          <Box sx={{ p: 0.5, maxWidth: 220 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              {geozona.nombre}
                            </Typography>
                            <Typography variant="body2" gutterBottom color="text.secondary">
                              {geozona.descripcion || 'Sin descripción'}
                            </Typography>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => deleteGeozona(geozona.id)}
                              sx={{ mt: 1 }}
                            >
                              Eliminar
                            </Button>
                          </Box>
                        </InfoWindow>
                      )}
                    </React.Fragment>
                  ))}
                  {drawingMode === 'polygon' && drawingPoints.length > 0 && (
                    <>
                      <Polygon
                        paths={drawingPoints}
                        options={drawingOptions.polygonOptions}
                      />
                      {drawingPoints.map((point, index) => (
                        <Marker key={index} position={point} />
                      ))}
                    </>
                  )}
                </GoogleMap>
              )}
            </Paper>

            <Paper
              elevation={2}
              sx={{
                flex: '0 1 380px',
                minWidth: 320,
                borderRadius: 3,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: MAP_HEIGHT,
                overflowY: 'auto'
              }}
            >
              {drawingMode === 'polygon' ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <EditLocationAltIcon color="primary" />
                    <Typography variant="h6">Dibujando Zona</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Hacé click en el mapa para agregar puntos al polígono. Necesitás al menos 3 puntos.
                  </Typography>
                  <Chip
                    label={`Puntos: ${drawingPoints.length}`}
                    color={drawingPoints.length >= 3 ? 'success' : 'default'}
                    sx={{ mb: 2, alignSelf: 'flex-start' }}
                  />
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={finishPolygon}
                      disabled={drawingPoints.length < 3}
                    >
                      Finalizar Polígono
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<UndoIcon />}
                      onClick={undoLastPoint}
                      disabled={drawingPoints.length === 0}
                    >
                      Deshacer Último Punto
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setConfirmDialogOpen(true)}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                </Box>
              ) : isCreating ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <PolygonIcon color="primary" />
                    <Typography variant="h6">Crear Nueva Zona</Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    label="Nombre de la zona"
                    variant="outlined"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Descripción (opcional)"
                    variant="outlined"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    sx={{ mb: 2 }}
                    multiline
                    rows={3}
                  />
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={saveGeozona}
                      disabled={!nombre}
                    >
                      Guardar Zona
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setConfirmDialogOpen(true)}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                      Zonas Guardadas ({geozonas.length})
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => startCreatingZone('polygon')}
                      startIcon={<PolygonIcon />}
                    >
                      Nueva Zona
                    </Button>
                  </Stack>

                  {geozonas.length === 0 ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      py: 6,
                      color: 'text.secondary'
                    }}>
                      <MapIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                      <Typography variant="body2">
                        No hay zonas guardadas para este grupo
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {geozonas.map((geozona, index) => (
                        <React.Fragment key={geozona.id}>
                          {index > 0 && <Divider component="li" />}
                          <ListItem
                            sx={{ px: 0 }}
                            secondaryAction={
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Ver en el mapa">
                                  <IconButton size="small" color="primary" onClick={() => onShapeClick(geozona)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton size="small" color="error" onClick={() => deleteGeozona(geozona.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{
                                bgcolor: geozona.type === 'circle' ? CIRCLE_COLOR : POLYGON_COLOR,
                                width: 36,
                                height: 36
                              }}>
                                {geozona.type === 'circle'
                                  ? <CircleIcon fontSize="small" />
                                  : <PolygonIcon fontSize="small" />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={geozona.nombre}
                              secondary={geozona.descripcion || 'Sin descripción'}
                              primaryTypographyProps={{ fontWeight: 600 }}
                              secondaryTypographyProps={{ noWrap: true }}
                              sx={{ pr: 8 }}
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      {/* Diálogo de confirmación para cancelar */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>¿Cancelar creación de zona?</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro que deseas cancelar la creación de esta zona? Se perderán los cambios no guardados.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Continuar Editando</Button>
          <Button onClick={confirmCancelDrawing} color="error" autoFocus>
            Sí, Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GeoZonas;
